import React from 'react';
import { Input, Loader } from 'freemium-ui';
import Downshift, { GetItemPropsOptions } from 'downshift';
import { get, throttle } from 'lodash';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { MIN_SEARCH_LIMIT } from 'routes/constants';
import { AccountInfoType } from '../../../shared/types';
import Icon from '../../../shared/components/Icon';
import { alphabetColors } from '../../../shared/constants';
import { useGetContact } from '../../screens/contacts/hooks';
import { useGetContactList } from '../../screens/lists/hooks';
import { useGetSegment } from '../../screens/segments/hooks';
import { SearchByType, SearchResultType, useSearch } from './hooks';

type SearchInputProps = {
  searchBy: SearchByType;
};

const SearchInput = (props: SearchInputProps) => {
  const { t } = useTranslation();
  const { searchBy = 'contact' } = props;
  const history = useHistory();

  const queryClient = useQueryClient();

  const { mcr_account_id }: AccountInfoType =
    queryClient.getQueryData('accountinfo') || {};

  const [searchText, setSearchText] = React.useState<string>('');
  const [result, setResult] = React.useState<SearchResultType>({
    ids: [],
    type: 'contact',
  });

  const searchPlaceholder = () => {
    if (searchBy === 'list') {
      return `${t('forms.placeholders.lists_search_placeholder')}`;
    } else if (searchBy === 'segment') {
      return `${t('forms.placeholders.segments_search_placeholder')}`;
    } else {
      return `${t('forms.placeholders.contacts_search_placeholder')}`;
    }
  };

  const { refetch: search, isFetching: isSearching } = useSearch({
    search_by: searchBy,
    search_value: searchText,
    enabled: !!searchText && searchText?.length >= MIN_SEARCH_LIMIT,
    callback: (isSuccess: boolean, response: any) => {
      if (isSuccess && response) {
        setResult(response);
      }
    },
  });

  const throttled = React.useRef(
    throttle((str: string) => {
      if (!mcr_account_id || !str || str.length < MIN_SEARCH_LIMIT) return;
      search();
    }, 1000),
  );

  React.useEffect(() => throttled.current(searchText), [searchText]);

  return (
    <Downshift
      onInputValueChange={(val: string) => setSearchText(val)}
      inputValue={searchText || ''}
      itemCount={result?.ids?.length}
      initialIsOpen
      onChange={(selectId) => {
        switch (searchBy) {
          case 'contact':
            // if (selectId) window.open(`/contacts/${selectId}`);
            if (selectId) history.push(`/contacts/${selectId}`);
            break;
          case 'list':
            // if (selectId) window.open(`/lists/${selectId}`);
            if (selectId) history.push(`/lists/${selectId}`);
            break;
          case 'segment':
            // if (selectId) window.open(`/segments/${selectId}/edit`);
            if (selectId) history.push(`/segments/${selectId}/edit`);
            break;
        }
      }}
      itemToString={(item) => (item ? item.value : '')}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        inputValue,
        highlightedIndex,
        clearSelection,
        getRootProps,
      }) => (
        <SearchInputContainer>
          <div style={{ display: 'inline-block' }} {...getRootProps()}>
            <Input
              {...getInputProps()}
              startAdornment={
                <Adornment pd="0 0 0 12px">
                  <Icon width="14" height="14" glyph="search" />
                </Adornment>
              }
              endAdornment={
                <Adornment mw="40px">
                  {isSearching ? (
                    <Loader size="small" />
                  ) : (
                    <>
                      {inputValue !== '' && (
                        <Icon
                          width="12"
                          height="12"
                          glyph="close"
                          onClick={() =>
                            clearSelection(() => setSearchText(''))
                          }
                        />
                      )}
                    </>
                  )}
                </Adornment>
              }
              placeholder={searchPlaceholder()}
              maxLength={50}
            />
          </div>
          {isOpen && inputValue && (
            <>
              {inputValue?.length >= MIN_SEARCH_LIMIT ? (
                <>
                  {(result?.ids || []).length > 0 ? (
                    <ResultContainer {...getMenuProps()}>
                      {(result?.ids || []).map((r: string, index: number) => (
                        <SearchResult
                          highlightedIndex={highlightedIndex}
                          key={index}
                          index={index}
                          getItemProps={getItemProps}
                          id={r}
                          type={result.type}
                        />
                      ))}
                    </ResultContainer>
                  ) : (
                    <ResultContainer>
                      <ResultItem>
                        <div className="name-tag">-</div>
                        <div className="contact-data">
                          <div className="contact-name">
                            {t('nothing_found')}
                          </div>
                          <div className="contact-email">
                            {t('empty_state.message.new_search')}
                          </div>
                        </div>
                      </ResultItem>
                    </ResultContainer>
                  )}
                </>
              ) : (
                <ResultContainer>
                  <ResultItem>
                    <div className="name-tag">-</div>
                    <div className="contact-data">
                      <div className="contact-name">
                        {t('forms.err_msg.enter_char_to_search', {
                          limit: MIN_SEARCH_LIMIT,
                        })}
                      </div>
                      <div className="contact-email">
                        {t('empty_state.message.new_search')}
                      </div>
                    </div>
                  </ResultItem>
                </ResultContainer>
              )}
            </>
          )}
        </SearchInputContainer>
      )}
    </Downshift>
  );
};

const getText = (type: SearchByType, data: any) => {
  switch (type) {
    case 'contact':
      return `${get(data, ['first_name'], '') || ''} ${
        get(data, ['firslast_namet_name'], '') || ''
      }`.trim();
    case 'list':
      return `${get(data, ['label'], '') || ''}`.trim();
    case 'segment':
      return `${get(data, ['name'], '') || ''}`.trim();
  }
};

const getSubText = (type: SearchByType, data: any) => {
  switch (type) {
    case 'contact':
      return `${get(data, ['work_email'], '') || ''}`.trim();
    case 'list':
      return '';
    case 'segment':
      return '';
  }
};

const SearchResult = (props: {
  type: SearchByType;
  id: string;
  index: number;
  getItemProps: (options: GetItemPropsOptions<any>) => any;
  highlightedIndex: number | null;
}) => {
  const queryClient = useQueryClient();
  const { id, type, highlightedIndex, getItemProps, index } = props;
  const [data, setData] = React.useState<any>({});

  const { isFetching: isFetchingContact, refetch: refetchContact } =
    useGetContact(type === 'contact' ? id || '' : '', {
      callback: (isSuccess: boolean, response: any) => {
        if (isSuccess) {
          setData(response);
        }
      },
    });

  const { isFetching: isFetchingContactList, refetch: refetchContactList } =
    useGetContactList({
      contactListId: type === 'list' ? id || '' : '',
      callback: (isSuccess: boolean, response: any) => {
        if (isSuccess) {
          setData(response);
        }
      },
    });

  const { isFetching: isFetchingSegment, refetch: refetchSegment } =
    useGetSegment({
      segmentId: type === 'segment' ? id || '' : '',
      callback: (isSuccess: boolean, response: any) => {
        if (isSuccess) {
          setData(response);
        }
      },
    });

  const isFetching =
    isFetchingContact || isFetchingContactList || isFetchingSegment;

  const processContactSearch = () => {
    const contact = queryClient.getQueryData(['contact', id]);
    if (contact) {
      setData(contact);
    } else {
      refetchContact();
    }
  };

  const processContactListSearch = () => {
    const contactList = queryClient.getQueryData(['contact-lists', id]);
    if (contactList) {
      setData(contactList);
    } else {
      refetchContactList();
    }
  };

  const processSegmentSearch = () => {
    const segment = queryClient.getQueryData(['segment', id]);
    if (segment) {
      setData(segment);
    } else {
      refetchSegment();
    }
  };

  React.useEffect(() => {
    if (type) {
      switch (type) {
        case 'contact':
          processContactSearch();
          break;
        case 'list':
          processContactListSearch();
          break;
        case 'segment':
          processSegmentSearch();
          break;
      }
    }
  }, [id]);

  const text = getText(type, data);
  const subText = getSubText(type, data);

  return isFetching ? (
    <ResultItem
      {...getItemProps({
        item: id,
        index,
        isSelected: highlightedIndex === index,
      })}
    >
      <div className="loading">
        <Skeleton height={40} />
      </div>
      <div className="contact-data">
        <div className="contact-name">
          <Skeleton height={20} />
        </div>
        <div className="contact-email">
          <Skeleton />
        </div>
      </div>
    </ResultItem>
  ) : (
    <ResultItem
      alphabet={(text || '').slice(0, 1)}
      className={highlightedIndex === index ? 'selected' : ''}
      {...getItemProps({
        item: id,
        index,
        isSelected: highlightedIndex === index,
      })}
    >
      <div className="name-tag">{(text || '').slice(0, 1)}</div>
      <div className="contact-data">
        <div className="contact-name">{text}</div>
        <div className="contact-email">{subText}</div>
      </div>
    </ResultItem>
  );
};

export const ResultItem = styled.div<{ alphabet?: string }>`
  :hover,
  &.selected {
    background-color: aliceblue;
  }
  padding: 8px;
  display: flex;
  align-items: center;
  div.loading {
    width: 42px;
    height: 42px;
    margin-right: 12px;
    border-radius: 4px;
  }
  div.name-tag {
    width: 42px;
    height: 42px;
    margin-right: 12px;
    background: ${({ alphabet }) =>
      alphabetColors[`${alphabet || ''}`.toLowerCase()] || alphabetColors.a};
    border-radius: 4px;
    text-align: center;
    line-height: 42px;
    user-select: none;
    font-size: 14px;
    color: #ffffff;
    opacity: 0.5;
    font-weight: bold;
  }
  div.contact-data {
    flex: 1;
    overflow: auto;
    div.contact-name {
      font-size: 13px;
      line-height: 16px;
      color: #12344d;
      margin-bottom: 6px;
      text-overflow: ellipsis;
      overflow: hidden;
    }
    div.contact-email {
      font-weight: 500;
      font-size: 12px;
      line-height: 14px;
      color: #12344d;
      opacity: 0.5;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
`;

export const ResultContainer = styled.div`
  position: absolute;
  background: #ffffff;
  box-shadow: 0px 2px 16px rgba(18, 52, 77, 0.16);
  border-radius: 4px;
  padding: 8px;
  width: 400px;
  margin-top: 4px;
  max-height: 80vh;
  overflow: auto;
`;

export const SearchInputContainer = styled.div`
  background-color: white;
  input {
    width: 212px;
  }
  .end-max-length {
    display: none;
  }
`;

export const Adornment = styled.div<{ pd?: string; mw?: string }>`
  min-width: ${({ mw }) => mw || 'unset'};
  padding: ${({ pd }) => pd || '0 12px'};
  display: grid;
  place-content: center;
  svg {
    cursor: pointer;
  }
`;

export default SearchInput;
